import * as yup from 'yup'

import {
  isFailureFlagNode,
  isInfraAttackNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { walkGraphUntilCondition } from 'components/scenarios/utils/graph/walkers'
import {
  getScenarioType,
  type ScenarioType
} from 'components/scenarios/utils/scenario-type'
import { type TranslationLanguage, trans } from 'i18n/translate'
import {
  type PolicyCategory,
  type PolicyCategoryLabel
} from 'types/api/policies/policy'
import {
  type GraphNode,
  type RecommendedScenario,
  type Scenario,
  type SharedScenario
} from 'types/api/scenarios/scenario'
import { type Team } from 'types/api/teams'
import {
  type TestSuite,
  type TestSuiteCreationRequest,
  type TestSuiteUpdateRequest
} from 'types/api/test-suites/test-suites'

import type React from 'react'

/**
 * Constants
 */
export const POLICY_CATEGORY_OPTIONS: readonly {
  label: PolicyCategoryLabel
  value: PolicyCategory
}[] = [
  { label: 'Redundancy', value: 'REDUNDANCY' },
  { label: 'Scalability', value: 'SCALABILITY' },
  { label: 'Dependencies', value: 'DEPENDENCIES' },
  { label: 'Other', value: 'OTHER' }
]

export const USER_MESSAGES = {
  scenarioInfoUnknown:
    "Some information is missing due to this scenario being owned by a team you don't have access to"
} as const

/**
 * Form-specific types
 */

// Should contain everything necessary to create a Reliability Test
export interface TestSuiteScenario {
  name: string // Name of Reliability Test/Policy, default to Scenario name
  description: string // default to Scenario description
  reliabilityTestId: string | null // If not new, it'll have an existing Policy
  policyCategory: PolicyCategory
  scenarioId: string
  scenarioType: ScenarioType
}

export interface TestSuiteTestRow extends TestSuiteScenario {
  owningTeamLabel: string | null
  scenario?: ScenarioForTestSuite
}

export type TestSuiteFormValues = {
  name: string
  targetTeamIds: string[]
  scenarios: TestSuiteScenario[]
  excludedRiskIds: string[]
}

export type TestSuiteTestDetailsFormValues = {
  name: string
  description: string
  policyCategory: PolicyCategory
  reliabilityTestId: string | null
}

export type ScenarioForTestSuite =
  | Scenario
  | SharedScenario
  | RecommendedScenario

/**
 * Validation schemas
 */
export function getTestSuiteValidationSchema() {
  return yup.object().shape({
    name: yup.string().trim().required('Name is required'),
    targetTeamIds: yup.array().of(yup.string()),
    scenarios: yup
      .array()
      .of(yup.mixed<TestSuiteScenario>())
      .min(1, 'At least one test is required'),
    excludedRisks: yup.array().of(yup.string())
  })
}

export function getTestSuiteTestDetailsValidationSchema() {
  return yup.object().shape({
    name: yup.string().trim().required('Name is required'),
    description: yup.string().trim().required('Description is required'),
    policyCategory: yup
      .string()
      .oneOf(POLICY_CATEGORY_OPTIONS.map((option) => option.value))
      .required('Category is required')
  })
}

/**
 * Utility functions
 */

/**
 * Ensures both type and casing is correct
 */
export function ensureIsPolicyCategory(
  str: PolicyCategory | PolicyCategoryLabel
) {
  return str.toUpperCase() as PolicyCategory
}

export function getTestSuiteFormInitialValues(
  testSuite: TestSuite | undefined,
  lang: TranslationLanguage = 'en'
): TestSuiteFormValues {
  if (!testSuite) {
    return {
      name: '',
      targetTeamIds: [],
      scenarios: [],
      excludedRiskIds: []
    }
  }

  const {
    name,
    excludedRiskIds = [],
    identifier = null,
    targetTeamIds = [],
    testResponses
  } = testSuite

  const isClone = identifier === null

  const formValues: Omit<TestSuiteFormValues, 'scenarios'> = {
    name: isClone ? `${trans('clone', {}, lang)} - ${name}` : name,
    // A cloned test suite should initially have no teams.
    // A JIRA-style option to clone or not clone the teams could be provided in the future.
    targetTeamIds: isClone ? [] : targetTeamIds,
    excludedRiskIds
  }

  /**
   * Handle Edit / Clone
   */
  const scenarios: TestSuiteScenario[] = testResponses.map((testResponse) => ({
    name: testResponse.testPolicy.name,
    description: testResponse.testPolicy.description || '',
    policyCategory: ensureIsPolicyCategory(
      testResponse.testPolicy.policyCategory
    ),
    reliabilityTestId: testResponse.testPolicy.reliabilityTestId ?? null,
    scenarioId: testResponse.sourceScenarioId,
    scenarioType: testResponse.sourceScenarioType
  }))

  return {
    ...formValues,
    scenarios
  }
}

export function getDefaultValuesFromTestSuiteScenario(
  testSuiteScenario?: TestSuiteScenario,
  scenario?: ScenarioForTestSuite
): TestSuiteTestDetailsFormValues {
  if (!testSuiteScenario) {
    return {
      name: scenario?.name ?? '',
      description: scenario?.description ?? '',
      policyCategory: 'OTHER',
      reliabilityTestId: null
    }
  }

  const { name, description, reliabilityTestId, policyCategory } =
    testSuiteScenario

  return {
    name,
    description: description ?? '',
    policyCategory,
    reliabilityTestId
  }
}

export function getScenarioTypeForTestSuite(
  scenario:
    | TestSuiteScenario
    | Partial<Scenario>
    | Partial<RecommendedScenario>
    | Partial<SharedScenario>
): ScenarioType {
  if ('scenarioType' in scenario) {
    return scenario.scenarioType
  }

  return getScenarioType(scenario)
}

export function getOwningTeamLabel(
  scenarioTeamId: string | undefined,
  owningTeam?: Team
) {
  const owningTeamName = owningTeam && owningTeam.name

  if (owningTeamName) {
    return owningTeamName
  }

  if (scenarioTeamId) {
    return `id: ${scenarioTeamId}`
  }

  return null
}

export function getNewTestSuiteScenario(
  formValues: TestSuiteTestDetailsFormValues | undefined,
  testToEdit: TestSuiteScenario | undefined,
  scenario: Partial<Scenario> | undefined
): TestSuiteScenario {
  return {
    name: formValues?.name ?? scenario?.name ?? '',
    description: formValues?.description ?? scenario?.description ?? '',
    policyCategory: formValues?.policyCategory ?? 'OTHER',
    scenarioId: testToEdit?.scenarioId ?? scenario?.guid ?? '',
    scenarioType:
      testToEdit?.scenarioType ?? getScenarioTypeForTestSuite(scenario ?? {}),
    reliabilityTestId: testToEdit?.reliabilityTestId ?? null
  }
}

export function getNewTestSuiteWithPolicy(
  formValues: TestSuiteTestDetailsFormValues,
  testToEdit: TestSuiteScenario
): TestSuiteScenario {
  return {
    ...testToEdit, // scenarioId, scenarioType, reliabilityTestId
    name: formValues?.name,
    description: formValues?.description,
    policyCategory: formValues?.policyCategory
  }
}

export function generateTestSuitePostRequest(
  formValues: TestSuiteFormValues
): TestSuiteCreationRequest {
  const scenarios = formValues.scenarios.map((scenarioValue) => {
    return {
      description: scenarioValue.description,
      name: scenarioValue.name,
      policyCategory: scenarioValue.policyCategory,
      scenarioToPromote: {
        scenarioId: scenarioValue.scenarioId,
        scenarioType: getScenarioTypeForTestSuite(scenarioValue)
      }
    }
  })

  return {
    name: formValues.name,
    targetTeamIds: formValues.targetTeamIds,
    scenarios,
    excludedRiskIds: formValues.excludedRiskIds
  }
}

export function generateTestSuitePatchRequest(
  formValues: TestSuiteFormValues,
  isOfficialTestSuite: boolean
): Partial<TestSuiteUpdateRequest> {
  // we can only modify the team ids for official test suite
  if (isOfficialTestSuite) {
    return {
      targetTeamIds: formValues.targetTeamIds
    }
  }

  const scenarios = formValues.scenarios.map((scenarioValue) => {
    return {
      description: scenarioValue.description,
      name: scenarioValue.name,
      policyCategory: scenarioValue.policyCategory,
      reliabilityTestId: scenarioValue.reliabilityTestId,
      scenarioId: scenarioValue.scenarioId,
      scenarioType: scenarioValue.scenarioType
    }
  })

  return {
    name: formValues.name,
    targetTeamIds: formValues.targetTeamIds,
    scenarios,
    excludedRiskIds: formValues.excludedRiskIds
  }
}

export enum CreateTestSuiteFormViewName {
  AddTests = 'test_suite.forms.create_test_form.view_name.add_test',
  EditRisks = 'test_suite.forms.create_test_form.view_name.edit_risks',
  AddTeams = 'test_suite.forms.create_test_form.view_name.add_teams'
}

export interface CreateTestSuiteFormViewItem {
  name: CreateTestSuiteFormViewName
  order: number
  isOptional?: boolean
  isPrimaryButtonDisabled: boolean
  primaryButtonText: string
  primaryEndIcon?: React.ReactNode
  secondaryStartIcon?: React.ReactNode
  secondaryButtonText?: string
}

/** We assume that the test suite scenario is valid - has one target type */
export function getTestSuiteScenarioTargetingInfo(
  scenario: ScenarioForTestSuite
) {
  let hasFailureFlagNode = false
  let hasInfraNode = false

  walkGraphUntilCondition(scenario.graph, (node: GraphNode) => {
    if (hasFailureFlagNode || hasInfraNode) {
      return false
    }
    if (isInfraAttackNode(node)) {
      hasInfraNode = true
    }
    if (isFailureFlagNode(node)) {
      hasFailureFlagNode = true
    }
    return true
  })

  return {
    hasInfraNode,
    hasFailureFlagNode
  }
}

/** A scenario may have multiple target types */
export function getScenarioTargetingInfo(scenario: ScenarioForTestSuite) {
  let hasFailureFlagNode = false
  let hasInfraNode = false

  walkGraphUntilCondition(scenario.graph, (node: GraphNode) => {
    if (hasFailureFlagNode && hasInfraNode) {
      return false
    }
    if (isInfraAttackNode(node)) {
      hasInfraNode = true
    }
    if (isFailureFlagNode(node)) {
      hasFailureFlagNode = true
    }
    return true
  })

  return {
    hasInfraNode,
    hasFailureFlagNode
  }
}

/**
 * A scenario is eligible to be promoted to a test suite if it has _either_ failure flag or infra nodes - it can't have both.
 */
export function hasValidScenarioTargets(scenario: ScenarioForTestSuite) {
  const { hasFailureFlagNode, hasInfraNode } =
    getScenarioTargetingInfo(scenario)
  return !(hasFailureFlagNode && hasInfraNode)
}
